// Decide whether the email-campaign dashboard should keep auto-refreshing.
//
// IMPORTANT: this decision is a function of BOTH the campaign data AND the
// current wall-clock time. A "scheduled" campaign becomes in-flight only once
// its send time arrives — so callers MUST re-evaluate this on a timer with a
// fresh `nowMs`, never cache it against the data alone. (The original bug: it
// was memoised on the data snapshot, so a campaign scheduled while the page was
// open never started polling and the list stayed stale until a manual refresh.)

// Normalize bare UTC API strings (e.g. "2026-06-10 12:37:00") → ISO with Z.
// Mirrors the helper in EmailBroadcastsDashboard so parsing matches the display.
function toUtcIso(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return (s.includes(" ") ? s.replace(" ", "T") : s) + "Z";
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// How early (ms) before its scheduled time a campaign is treated as "due" so we
// start polling just before the worker is expected to pick it up.
const DUE_BUFFER_MS = 90 * 1000;

// Statuses that are still moving toward a terminal state.
const ACTIVE_STATUSES = new Set(["sending", "queued", "processing", "pending"]);

/**
 * Is this single campaign still in flight (its final status not yet reflected)?
 * @param {object} c        campaign row from the dashboard calendar payload
 * @param {number} nowMs    current time in epoch ms (pass Date.now() at call site)
 */
export function isCampaignInFlight(c, nowMs = Date.now()) {
  const status = String(c?.status || "").toLowerCase();

  if (ACTIVE_STATUSES.has(status)) return true;

  // Recipients still queued means the worker hasn't finished, regardless of status.
  if (num(c?.counts?.queued) > 0) return true;

  if (status === "scheduled") {
    const iso = toUtcIso(c?.schedule_at);
    const at = iso ? new Date(iso).getTime() : NaN;
    // Poll only once it is due/overdue — a far-future schedule isn't moving yet.
    return Number.isFinite(at) ? at <= nowMs + DUE_BUFFER_MS : false;
  }

  return false;
}

/**
 * Should the dashboard re-fetch right now? True if ANY campaign is in flight.
 * @param {Array}  items   dashboard calendar items
 * @param {number} nowMs   current time in epoch ms
 */
export function shouldPollCampaigns(items, nowMs = Date.now()) {
  return (Array.isArray(items) ? items : []).some((c) => isCampaignInFlight(c, nowMs));
}
